import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import { API_URL } from '../config';
import './MyNetworkPage.css';

const MyNetworkPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('tree');
  const [networkData, setNetworkData] = useState(null);
  const [legsData, setLegsData] = useState(null);
  const [networkTree, setNetworkTree] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Use allSettled so Tree View failures don't break Overview / Legs for "some users".
      const results = await Promise.allSettled([
        axios.get(`${API_URL}/Referral/my-network`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/Referral/my-legs`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/Referral/my-referral-code`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/Referral/my-network-tree`, { headers: getAuthHeader() })
      ]);

      const networkRes = results[0].status === 'fulfilled' ? results[0].value : null;
      const legsRes = results[1].status === 'fulfilled' ? results[1].value : null;
      const referralRes = results[2].status === 'fulfilled' ? results[2].value : null;
      const treeRes = results[3].status === 'fulfilled' ? results[3].value : null;

      // If core data is missing, show error; otherwise keep page usable.
      if (!networkRes || !legsRes || !referralRes) {
        throw new Error('Failed to load core network data');
      }

      // Normalize data to handle $values wrapper from .NET API
      const normalizeArray = (arr) => Array.isArray(arr) ? arr : (arr?.$values || []);
      const normalizeTreeNode = (node) => {
        if (!node) return null;
        const children = normalizeArray(node.children ?? node.Children);
        return {
          id: node.id ?? node.Id,
          userCode: node.userCode ?? node.UserCode ?? node.referralCode ?? node.ReferralCode ?? null,
          name: node.name ?? node.Name,
          joiningDate: node.joiningDate ?? node.JoiningDate,
          contactNumber: node.contactNumber ?? node.ContactNumber,
          kycStatus: node.kycStatus ?? node.KycStatus,
          email: node.email ?? node.Email,
          level: node.level ?? node.Level,
          children: children.map(normalizeTreeNode),
        };
      };
      
      const network = networkRes.data;
      if (network) {
        network.levelCounts = normalizeArray(network.levelCounts);
        network.directReferrals = normalizeArray(network.directReferrals);
        network.downline = normalizeArray(network.downline);
      }
      setNetworkData(network);

      const legs = legsRes.data;
      if (legs) {
        const rawLegs = normalizeArray(legs.legs);
        legs.legs = rawLegs.map((leg) => {
          const rawMembers = normalizeArray(leg.members ?? leg.Members);
          return {
            ...leg,
            legRootId: leg.legRootId ?? leg.LegRootId,
            legRootName: leg.legRootName ?? leg.LegRootName,
            legRootEmail: leg.legRootEmail ?? leg.LegRootEmail,
            legRootPhoneNumber: leg.legRootPhoneNumber ?? leg.LegRootPhoneNumber,
            legRootKycStatus: leg.legRootKycStatus ?? leg.LegRootKycStatus,
            legRootJoinedAt: leg.legRootJoinedAt ?? leg.LegRootJoinedAt,
            members: rawMembers.map((m) => ({
              descendantUserId: m.descendantUserId ?? m.DescendantUserId,
              level: m.level ?? m.Level,
              username: m.username ?? m.Username,
              userCode: m.userCode ?? m.UserCode,
              pointsEarned: m.pointsEarned ?? m.PointsEarned ?? 0,
              email: m.email ?? m.Email,
              phoneNumber: m.phoneNumber ?? m.PhoneNumber,
              kycStatus: m.kycStatus ?? m.KycStatus,
              joinedAt: m.joinedAt ?? m.JoinedAt,
            })),
            levelBreakdown: normalizeArray(leg.levelBreakdown ?? leg.LevelBreakdown),
          };
        });
      }
      setLegsData(legs);

      // Tree View is optional; if it fails, keep other tabs working.
      if (treeRes?.data) {
        const normalizedTree = normalizeTreeNode(treeRes.data);
        setNetworkTree(normalizedTree);
        setExpandedIds(normalizedTree?.id ? new Set([normalizedTree.id]) : new Set());
      } else {
        setNetworkTree(null);
        setExpandedIds(new Set());
      }

      setReferralInfo(referralRes.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching network data:', err);
      setError('Failed to load network data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = async () => {
    if (referralInfo?.referralLink) {
      try {
        await navigator.clipboard.writeText(referralInfo.referralLink);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const sendInvitation = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;

    setInviting(true);
    try {
      await axios.post(`${API_URL}/Referral/send-invitation`, 
        { email: inviteEmail },
        { headers: getAuthHeader() }
      );
      alert(`Invitation sent to ${inviteEmail}! Share your referral link with them.`);
      setInviteEmail('');
    } catch (err) {
      alert(err.response?.data?.message || err.response?.data || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  // Flatten all legs; member contact/KYC/email only when level === 1 (direct referral). Leg root is always direct.
  const legsRows = legsData?.legs?.flatMap((leg) =>
    (() => {
      const members = leg.members || [];
      const legPointsEarned = members
        .filter((x) => Number(x.level) >= 1 && Number(x.level) <= 7)
        .reduce((sum, x) => sum + Number(x.pointsEarned || 0), 0);
      return members.map((m) => ({
        userCode: m.userCode,
        legRootUserId: leg.legRootId,
        legEmail: leg.legRootEmail,
        legPhoneNumber: leg.legRootPhoneNumber,
        legKycStatus: leg.legRootKycStatus,
        level: m.level,
        username: m.username,
        // For this screen (direct referrals only), points = total earned from entire leg up to 7 levels.
        pointsEarned: Number(m.level) === 1 ? legPointsEarned : Number(m.pointsEarned || 0),
        email: m.email,
        contactNumber: m.phoneNumber,
        kycStatus: m.kycStatus,
        joinedAt: m.joinedAt,
        descendantUserId: m.descendantUserId,
      }));
    })()
  ) || [];

  // Robust fallback: some legacy users have sparse my-legs members;
  // build rows from my-network downline + direct referrals so table is not empty.
  const fallbackRowsFromNetwork = (() => {
    const downline = networkData?.downline || [];
    if (downline.length === 0) return [];

    const directRefsById = new Map(
      (networkData?.directReferrals || []).map((d) => [
        d.userId,
        {
          name: d.username,
          email: d.email,
          phoneNumber: d.phoneNumber,
          kycStatus: d.kycStatus || 'NotSubmitted',
        },
      ])
    );

    const legPointsByRoot = downline.reduce((acc, item) => {
      const legRoot = item.legRootUserId;
      const lvl = Number(item.level);
      if (!legRoot || lvl < 1 || lvl > 7) return acc;
      acc[legRoot] = (acc[legRoot] || 0) + Number(item.pointsEarned || 0);
      return acc;
    }, {});

    return downline.map((d) => {
      const legRoot = directRefsById.get(d.legRootUserId) || {};
      return {
        userCode: d.userCode || '-',
        legRootUserId: d.legRootUserId,
        legEmail: legRoot.email || null,
        legPhoneNumber: legRoot.phoneNumber || null,
        legKycStatus: legRoot.kycStatus || 'NotSubmitted',
        level: d.level,
        username: d.username,
        pointsEarned: Number(d.level) === 1
          ? Number(legPointsByRoot[d.legRootUserId] || 0)
          : Number(d.pointsEarned || 0),
        email: d.email,
        contactNumber: d.phoneNumber,
        kycStatus: d.kycStatus,
        joinedAt: d.joinedAt,
        descendantUserId: d.descendantUserId,
      };
    });
  })();

  const allDownlineRows = legsRows.length > 0 ? legsRows : fallbackRowsFromNetwork;
  const downlineTableRows = allDownlineRows.filter((row) => row.level === 1);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Flatten tree into rows for table (respecting expand/collapse)
  const getTreeRows = (node, depth = 0) => {
    if (!node) return [];
    const rows = [{ ...node, depth }];
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedIds.has(node.id);
    if (hasChildren && isExpanded) {
      node.children.forEach((child) => {
        rows.push(...getTreeRows(child, depth + 1));
      });
    }
    return rows;
  };

  const treeTableRows = networkTree ? getTreeRows(networkTree) : [];
  // Show exactly 8 levels in overview: Self as Level 1, then API levels 1..7 as Levels 2..8.
  const shiftedOverviewRows = [
    { key: 'self', label: 'Self (Level 1)', count: 1, isSelf: true },
    ...((networkData?.levelCounts || [])
      .filter((level) => Number(level.level) >= 1 && Number(level.level) <= 7)
      .map((level) => ({
        key: `lvl-${level.level}`,
        label: `Level ${Number(level.level) + 1}`,
        count: Number(level.count || 0),
        isSelf: false,
      }))),
  ];
  const overviewMaxCount = Math.max(1, ...shiftedOverviewRows.map((row) => row.count));

  if (loading) {
    return (
      <div className="network-wrapper">
        <Header />
        <div className="network-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading your network...</p>
          </div>
        </div>
      </div>
    );
  }

  // Invite is allowed at any level; benefits (PV) apply only to Level 1–8 (see OrdersController/OfflineTransactionController).

  return (
    <div className="network-wrapper">
      <Header />
      <div className="network-container">
        <div className="network-header">
          <h1>My Network</h1>
          <p>Manage your referral network and grow your team</p>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {/* Referral Link Section */}
        <div className="referral-link-card">
          <div className="referral-link-header">
            <h3>Your Referral Link</h3>
          </div>
          <div className="referral-link-content">
            <div className="link-box">
              <input 
                type="text" 
                value={referralInfo?.referralLink || ''} 
                readOnly 
              />
              <button 
                className={`copy-btn ${copySuccess ? 'copied' : ''}`}
                onClick={copyReferralLink}
              >
                {copySuccess ? (
                  <>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                    </svg>
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className="referral-code">
              Code: <strong>{referralInfo?.referralCode}</strong>
            </p>
          </div>

          {/* Invite Form – members can be added at any depth; PV benefits apply to Level 1–8 only */}
          <form className="invite-form" onSubmit={sendInvitation}>
            <input
              type="email"
              placeholder="Enter email to invite"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
            />
            <button type="submit" disabled={inviting}>
              {inviting ? 'Sending...' : 'Send Invite'}
            </button>
          </form>
          <p className="benefit-note">PV benefits apply to Level 1 (you) through Level 8 only. You can still add members below you at any depth.</p>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon direct">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-value">{networkData?.directReferralsCount || 0}</span>
              <span className="stat-label">Direct Referrals (Legs)</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon total">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-value">{networkData?.totalDownlineCount || 0}</span>
              <span className="stat-label">Total Network</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="network-tabs">
          <button 
            className={`tab-btn ${activeTab === 'tree' ? 'active' : ''}`}
            onClick={() => setActiveTab('tree')}
          >
            Tree View
          </button>
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Level Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'table' ? 'active' : ''}`}
            onClick={() => setActiveTab('table')}
          >
            My Direct Refferals
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'tree' && (
            <div className="network-tree-section">
              <h3>My Network – Tree View</h3>
              <p className="tree-privacy-note">Contact number, KYC status, and email are shown only for you and your direct referrals.</p>
              {treeTableRows.length > 0 ? (
                <div className="table-wrap tree-table-wrap">
                  <table className="network-tree-table">
                    <thead>
                      <tr>
                        <th className="col-expand"></th>
                        <th>User ID Code</th>
                        <th>Name</th>
                        <th>Joining Date</th>
                        <th>Contact Number</th>
                        <th>KYC Status</th>
                        <th>Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {treeTableRows.map((row) => {
                        const hasChildren = row.children && row.children.length > 0;
                        const isExpanded = expandedIds.has(row.id);
                        const showPrivateDetails = row.level === 0 || row.level === 1;
                        return (
                          <tr key={row.id} className="tree-row" data-depth={row.depth}>
                            <td className="col-expand">
                              {hasChildren ? (
                                <button
                                  type="button"
                                  className="tree-toggle"
                                  onClick={() => toggleExpanded(row.id)}
                                  aria-expanded={isExpanded}
                                >
                                  {isExpanded ? '−' : '+'}
                                </button>
                              ) : (
                                <span className="tree-toggle-placeholder"></span>
                              )}
                            </td>
                            <td className="col-id">{row.userCode || row.id}</td>
                            <td className="col-name" style={{ paddingLeft: `${row.depth * 20 + 8}px` }}>
                              {row.name || '–'}
                            </td>
                            <td className="col-date">{formatDate(row.joiningDate)}</td>
                            <td className="col-contact">{showPrivateDetails ? (row.contactNumber || '–') : '—'}</td>
                            <td className="col-kyc">
                              {showPrivateDetails ? (
                                <span className={`kyc-badge kyc-${(row.kycStatus || 'NotSubmitted').toLowerCase().replace(/\s+/g, '')}`}>
                                  {row.kycStatus || 'NotSubmitted'}
                                </span>
                              ) : (
                                <span className="tree-private-placeholder">—</span>
                              )}
                            </td>
                            <td className="col-email">{showPrivateDetails ? (row.email || '–') : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No network data yet. Share your referral link to grow your team!</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="overview-section">
              <h3>Members by Level</h3>
              {(networkData?.levelCounts?.length > 0 || true) ? (
                <div className="level-bars">
                  {shiftedOverviewRows.map((row) => {
                    const widthPercent = (row.count / overviewMaxCount) * 100;
                    return (
                      <div key={row.key} className="level-bar-row">
                        <span className="level-label">{row.label}</span>
                        <div className="level-bar-container">
                          <div
                            className={`level-bar ${row.isSelf ? 'self-bar' : ''}`}
                            style={{ width: `${widthPercent}%` }}
                          ></div>
                        </div>
                        <span className="level-count">{row.count}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No members in your network yet. Share your referral link to grow your team!</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'table' && (
            <div className="downline-table-section">
              <h3>My Direct Refferals</h3>
              {downlineTableRows.length > 0 ? (
                <div className="table-wrap">
                  <table className="downline-table">
                    <thead>
                      <tr>
                        <th>User Code</th>
                        <th>Member</th>
                        <th>Contact Number</th>
                        <th>KYC Status</th>
                        <th>Email</th>
                        <th>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {downlineTableRows.map((row) => {
                        const showMemberPrivate = row.level === 1;
                        return (
                          <tr key={`${row.userCode || '-'}-${row.descendantUserId}-${row.level}`}>
                            <td>
                              <span className="member-name">{row.userCode || '–'}</span>
                            </td>
                            <td>
                              <div className="member-info">
                                <span className="member-name">{row.username}</span>
                              </div>
                            </td>
                            <td>
                              {showMemberPrivate ? row.contactNumber || '–' : '—'}
                            </td>
                            <td>
                              {showMemberPrivate ? (
                                <span
                                  className={`kyc-badge kyc-${(row.kycStatus || 'NotSubmitted').toLowerCase().replace(/\s+/g, '')}`}
                                >
                                  {row.kycStatus || 'NotSubmitted'}
                                </span>
                              ) : (
                                <span className="tree-private-placeholder">—</span>
                              )}
                            </td>
                            <td>{showMemberPrivate ? row.email || '–' : '—'}</td>
                            <td>{formatDate(row.joinedAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No downline yet. Share your referral link to grow your team!</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyNetworkPage;
